<template>
  <div class="zm-captcha" v-show="!canSkipCaptcha">
    <div
      class="zm-captcha--smart-captcha"
      v-if="type === 'input' || canSkipCaptcha"
    >
      <zm-form
        :model="ruleForm"
        :rules="{ captchaText: rulesList }"
        ref="ruleForm"
        :validate-on-rule-change="false"
      >
        <zm-form-item
          ref="captchaText"
          prop="captchaText"
          :for="_formInputId"
          :error="errorMessage"
        >
          <zm-input
            ref="input"
            class="slot-input"
            :size="size"
            aria-label="input zoom captcha"
            v-model="ruleForm.captchaText"
            :placeholder="placeholder"
            :inline-label="inlineLabel"
            @keyup.enter.native="onInputEnter"
            @change="onInputChange"
            @input="onInput"
            :id="_formInputId"
            :aria-required="true"
            autofocus
          />
        </zm-form-item>
      </zm-form>
      <div
        :class="[
          size ? 'zm-captcha--' + size : '',
          inlineLabel ? 'zm-captcha--xLarge': '',
          'zm-operate-section',
          'mgl-sm',
        ]"
      >
        <div class="zm-captcha-action">
          <span>
            <zm-button
              type="icon"
              size="mini"
              ghost
              icon="zm-icon-refresh"
              class="captcha-refresh"
              @click="refresh"
              aria-label="refresh captcha"
            />
          </span>
          <span>
            <zm-button
              type="icon"
              size="mini"
              ghost
              class="captcha-switch"
              :icon="showImage ? 'zm-icon-volume-up' : 'zm-icon-text'"
              :aria-label="
                showImage
                  ? 'Switch to Audio Captcha'
                  : 'Switch to Visual Captcha'
              "
              @click="changeToAudio"
            />
          </span>
        </div>
        <div class="zm-captcha-display">
          <img
            v-show="showImage"
            class="zm-captcha-img"
            :src="imgUrl"
            :data-version="isGif ? '2' : ''"
            :data-type="layout === 'level' ? '1' : ''"
            alt="image"
          />
          <div v-show="!showImage" class="zm-captcha-audio">
            <zm-button
              size="mini"
              ghost
              @click="playAudio"
              type="link"
              aria-label="Play Audio Captcha"
              ref="playAudioButton"
            >
              <i class="zm-icon-play"></i>
              {{ t('captcha.playaudio') }}
            </zm-button>
            <!-- eslint-disable vuejs-accessibility/media-has-caption -->
            <audio
              ref="audio"
              type="audio/x-wav"
              :src="audioUrl"
              autostart="0"
            />
          </div>
          <!-- eslint-enable -->
        </div>
      </div>
    </div>
    <div v-else-if="type !== 'invisible'">
      <div class="checkbox-captcha" v-loading="loading">
        <recaptcha
          :is-grecaptcha-ent="isGrecaptchaEnt"
          ref="recaptcha"
          :is-cn="isCn"
          :is-hcaptcha="isHcaptcha"
          :sitekey="siteKey"
          :language="language"
          :theme="theme"
          tabindex
          @render="render"
          @verify="verify"
          @expired="expired"
          @error="error"
        />
      </div>
    </div>
    <div v-else>
      <p class="recaptcha-policy">
        <!-- eslint-disable vue/no-v-html -->
        <span
          v-if="!isHcaptcha"
          v-html="t('google.invisible.captcha.policy')"
        />
        <span v-else v-html="t('hcaptcha.invisible.captcha.policy')"></span>
        <!-- eslint-enable -->
        <!-- only support for extension team -->
        <slot name="more-policy"></slot>
      </p>
      <recaptcha
        :is-grecaptcha-ent="isGrecaptchaEnt"
        ref="recaptcha"
        :sitekey="siteKey"
        size="invisible"
        :is-cn="isCn"
        :is-hcaptcha="isHcaptcha"
        :language="language"
        :theme="theme"
        tabindex
        @render="render"
        @verify="verify"
        @expired="expired"
        @error="error"
      />
    </div>
  </div>
</template>
<script>
  import Recaptcha from './reCaptcha/Recaptcha';
  import { locale as myLang } from '../../locale';
  import Locale from '../../mixins/locale';
  import '../../../i18n/captcha';
  import { isSafari } from '../../utils/env';

  let _uid = 0;

  function uid() {
    return _uid++;
  }
  const captchaValueWhenCanSkip = '000000';
  export default {
    name: 'ZmCaptcha',
    mixins: [Locale],
    model: {
      prop: 'modelValue',
      event: 'changeVModelValue'
    },
    components: {
      Recaptcha
    },
    props: {
      modelValue: {
        type: String,
        default: ''
      },
      type: {
        type: String,
        validator: function(value) {
          let hasPassValid =
            ['input', 'checkbox', 'invisible'].indexOf(value) !== -1;
          if (hasPassValid) return true;
          console.error(
            'zm-captcha prop captacha-type must be input, checkbox or invisible' +
              value +
              'sgu'
          );
          return false;
        }
      },
      getExtImgUrl: {
        type: Function
      },
      getExtAudioUrl: {
        type: Function
      },
      theme: {
        type: String,
        default() {
          return 'light';
        }
      },
      isHcaptcha: {
        type: Boolean,
        default() {
          return window.isHcaptcha || false;
        }
      },
      isCn: {
        type: Boolean,
        default() {
          return window.isCN || false;
        }
      },
      hCaptchaSiteKey: {
        type: String,
        default() {
          return window.hCaptchaSiteKey || '';
        }
      },
      checkboxSiteKey: {
        type: String,
        default() {
          return window.gRecaptchaVisible || '';
        }
      },
      invisibleSiteKey: {
        type: String,
        default() {
          return window.gRecaptchaInvisible || '';
        }
      },
      isGif: {
        type: Boolean,
        default: false
      },
      layout: {
        type: String,
        default: 'level'
      },
      size: {
        type: String,
        default: 'small'
      },
      placeholder: {
        type: String,
        default: ''
      },
      inlineLabel: {
        type: String,
        default: ''
      },
      routingUrl: {
        type: String,
        default() {
          return window.routingUrl || '';
        }
      },
      rules: {
        type: Array,
        default() {
          return [];
        }
      },
      errorMessage: {
        type: String,
        default: ''
      },
      isGrecaptchaEnt: {
        type: Boolean,
        default() {
          return window.isGrecaptchaEnt || false;
        }
      },
      canSkipCaptcha: {
        type: Boolean,
        default() {
          return window.canSkipCaptcha || false;
        }
      }
    },
    data() {
      return {
        id: '',
        imgUrl: '',
        audioUrl: '',
        showImage: true,
        loading: true,
        ruleForm: {
          captchaText: this.modelValue
        }
      };
    },
    computed: {
      form() {
        return this.$refs.ruleForm;
      },
      rulesList() {
        let list = [
          {
            required: true,
            message: this.t('captcha.needCaptcha'),
            trigger: 'blur'
          }
        ];
        list.push(...this.rules);
        return list;
      },
      siteKey() {
        if (this.isHcaptcha) {
          return this.hCaptchaSiteKey;
        }
        if (this.type === 'invisible') {
          return this.invisibleSiteKey;
        }
        if (this.type === 'checkbox') {
          return this.checkboxSiteKey;
        }
        return null;
      },
      _formInputId() {
        return this.id ? this.id : 'zm-captcha-' + uid();
      },
      language() {
        // https://developers.google.com/recaptcha/docs/language
        let zmLangCode = 'en';
        const langJson = [
          {
            code: 'en',
            value: 'en-US'
          },
          {
            code: 'es',
            value: 'es-ES'
          },
          {
            code: 'de',
            value: 'de-DE'
          },
          {
            code: 'zh-CN',
            value: 'zh-CN'
          },
          {
            code: 'zh-TW',
            value: 'zh-TW'
          },
          {
            code: 'fr',
            value: 'fr-FR'
          },
          {
            code: 'pt',
            value: 'pt-PT'
          },
          {
            code: 'ja',
            value: 'jp-JP'
          },
          {
            code: 'ru',
            value: 'ru-RU'
          },
          {
            code: 'ko',
            value: 'ko-KO'
          },
          {
            code: 'it',
            value: 'it-IT'
          }
        ];
        for (let p in langJson) {
          if (myLang === langJson[p].value) {
            zmLangCode = langJson[p].code;
          }
        }
        return zmLangCode;
      }
    },
    watch: {
      type(type) {
        this.refresh();
      },
      modelValue: {
        handler: function(newVal, oldVal) {
          if (this.canSkipCaptcha && newVal !== captchaValueWhenCanSkip) {
            this.$emit('changeVModelValue', captchaValueWhenCanSkip);
            return;
          }
          this.ruleForm.captchaText = newVal;
        },
        immediate: true
      }
    },
    methods: {
      render(id) {
        this.loading = false;
        this.$emit('render', id);
      },
      refresh() {
        if (this.canSkipCaptcha) return;
        if (this.type === 'input') {
          this.$emit('changeVModelValue', '');
          if (!this.showImage) {
            this.initHTML5Audio();
          } else {
            this.initCaptchaImg();
          }
        } else {
          this.resetReCaptche();
        }
      },
      genImgUrl() {
        let imgUrl = '';
        if (this.getExtImgUrl) {
          imgUrl = this.getExtImgUrl();
        } else {
          imgUrl = `/captcha-image${this.isGif ? 2 : ''}?${
            this.layout === 'level' ? 'type=1' : ''
          }&rnd=${Math.random()}${
            this.routingUrl !== '' ? '&' + this.routingUrl : ''
          }`;
        }
        return imgUrl;
      },
      genAudioUrl() {
        let audioUrl = '';
        if (this.getExtAudioUrl) {
          audioUrl = this.getExtAudioUrl();
        } else {
          audioUrl = `/captcha-audio?${'rnd=' + Math.random()}${
            this.routingUrl !== '' ? '&' + this.routingUrl : ''
          }`;
        }
        return audioUrl;
      },
      resetReCaptche() {
        if (this.canSkipCaptcha) return;
        //  Reset reCAPTCHA instance
        this.$emit('changeVModelValue', '');
        this.$refs.recaptcha && this.$refs.recaptcha.reset();
      },
      executeReCAPTCHA() {
        if (this.canSkipCaptcha) return;
        //  Invoke reCAPTCHA challenge
        this.$refs.recaptcha && this.$refs.recaptcha.execute();
      },
      changeToAudio() {
        this.showImage = !this.showImage;
        this.showImage ? this.initCaptchaImg() : this.initHTML5Audio();
      },
      initCaptchaImg() {
        this.imgUrl = this.genImgUrl();
      },
      initHTML5Audio() {
        this.audioUrl = this.genAudioUrl();
        if (this.audioUrl) {
          this.$refs.audio.pause();
          this.$nextTick(() => {
            this.$refs.playAudioButton.$el.focus();
          });
        }
      },
      playAudio() {
        if (isSafari) {
          this.initHTML5Audio();
          this.$nextTick(() => {
            this.$refs.audio && this.$refs.audio.play();
          });
          return false;
        }
        this.$refs.audio && this.$refs.audio.play();
      },
      verify(res) {
        let finalData = res;
        if (this.isHcaptcha && this.type !== 'input') {
          finalData = 'hcaptcha:' + res;
        }
        this.$emit('verify', finalData);
        this.$emit('changeVModelValue', finalData);
      },
      expired() {
        this.$emit('expired');
        this.refresh();
      },
      error(err) {
        this.$emit('error', err);
      },
      onInputChange(val) {
        this.$emit('change', val);
      },
      onInputEnter(event) {
        this.$emit('enter', this.ruleForm.captchaText, event);
      },
      onInput(val) {
        this.$refs.captchaText && this.$refs.captchaText.clearValidate();
        let finalData = val;
        if (this.isHcaptcha && this.type !== 'input') {
          finalData = 'hcaptcha:' + val;
        }
        this.$emit('input', finalData);
        this.$emit('changeVModelValue', finalData);
      },
      getValue() {
        return this.ruleForm.captchaText;
      },
      addErrorClass() {
        if (this.canSkipCaptcha) return;
        this.$nextTick(() => {
          this.$refs.recaptcha && this.$refs.recaptcha.$el &&
            this.$refs.recaptcha.$el.children[0] &&
            this.$refs.recaptcha.$el.children[0].classList.add('recaptcha-error');
        });
      },
      removeErrorClass() {
        if (this.canSkipCaptcha) return;
        this.$nextTick(() => {
          this.$refs.recaptcha && this.$refs.recaptcha.$el &&
            this.$refs.recaptcha.$el.children[0] &&
            this.$refs.recaptcha.$el.children[0].classList.remove(
              'recaptcha-error'
            );
        });
      },
      validate(callBack) {
        if (this.canSkipCaptcha) return;
        const hasCallBack = typeof callBack === 'function';
        if (this.type !== 'input') {
          if (this.getValue()) {
            hasCallBack && callBack(true);
            this.removeErrorClass();
          } else {
            hasCallBack && callBack(false);
            this.addErrorClass();
          }
        } else {
          this.$refs.ruleForm.validate(function(valid) {
            hasCallBack && callBack(valid);
          });
        }
      },
      focus() {
        this.$refs.input && this.$refs.input.focus();
      },
      blur() {
        this.$refs.input && this.$refs.input.blur();
      },
      select() {
        this.$refs.input && this.$refs.input.select();
      }
    },
    mounted() {
      if (this.type === 'input' && !this.canSkipCaptcha) {
        if (!this.showImage) {
          this.initHTML5Audio();
        } else {
          this.initCaptchaImg();
        }
      }
    }
  };
</script>
<style lang="scss">
  @import '../../scss/mixins/mixins';
  @import '../../scss/common/var';

  @include b(captcha) {
    .recaptcha-error {
      border: 1px solid $--color-border-danger;
    }

    .recaptcha-policy {
      color: #6E7680;
      font-size: 12px;
      margin: 0 auto;
    }

    .grecaptcha-badge {
      visibility: hidden;
    }

    .checkbox-captcha {
      height: 78px;
      width: 310px;
    }

    .zm-form-item__error {
      white-space: nowrap;
    }

    @include m(small) {
      height: $--input-height;
    }

    @include m(large) {
      height: $--input-lg-height;
    }

    @include m(smart-captcha) {
      display: flex;

      .zm-operate-section {
        display: flex;
        flex-direction: row-reverse;
        border: 1px solid $--color-border-light;
        border-radius: 8px;

        .zm-captcha-display {
          display: inline-block;
          width: 100px;

          .zm-captcha-audio {
            text-align: center;
            height: 100%;
            display: flex;
            align-items: center;
            margin-left: 12px;
          }

          .zm-captcha-img {
            max-width: 100%;
            max-height: 100%;
            height: 100%;
            border: 1px solid $--color-border-light;
            border-radius: 5px;
          }
        }

        .zm-captcha-action {
          margin: auto;
          width: 55px;
        }
        &.zm-captcha--xLarge{
          height: 48px;
        }
      }
    }
  }
  .zm-theme--dark{
    .zm-captcha--smart-captcha .zm-operate-section{
      border-color: #98a0a9;
    }
  }
</style>
